/**
 * Response contract for GET /api/stats/about-metrics (PlatexAPI StatsController).
 * Powers the redesigned (DEV-only) About page headline metric cards.
 *
 * Every value is a conservative, defensible public figure traceable to a real
 * production source. `value` is the precise underlying number (drives the
 * count-up animation); `display` is the vetted rounded string shown on
 * completion (e.g. "79M+"). `sourceStatus` is "live" (queried this refresh),
 * "cached" (served from vetted constants) or "fallback" (live query failed).
 *
 * The whole response is cached server-side; the UI additionally falls back to
 * ABOUT_METRICS_FALLBACK if the endpoint is unreachable.
 */

export type MetricSourceStatus = 'live' | 'cached' | 'fallback';

export interface AboutMetric {
  value: number;
  display: string;
  label: string;
  subtext: string;
  sourceStatus: MetricSourceStatus;
}

export interface AboutMetricsResponse {
  searchableRegistrations: AboutMetric;
  dealerPricesAnalysed: AboutMetric;
  historicSalesRecords: AboutMetric;
  yearsOfMarketData: AboutMetric;
  dealerSources: AboutMetric;
  plateInteractionsTracked: AboutMetric;
  updatedAt: string; // ISO timestamp
}

/**
 * Approved display order for the "The market, in numbers" section.
 * Single source of truth for ordering — the template iterates this array.
 */
export const ABOUT_METRIC_KEYS: (keyof Omit<AboutMetricsResponse, 'updatedAt'>)[] = [
  'searchableRegistrations',
  'historicSalesRecords',
  'dealerSources',
  'yearsOfMarketData',
  'dealerPricesAnalysed',
  'plateInteractionsTracked',
];

/**
 * Central client-side fallback. Mirrors the server's vetted constants so the
 * page is fully functional even if the API is unavailable. Keep these values in
 * sync with PlatexAPI AboutMetricsConfig.BuildDefault.
 */
export const ABOUT_METRICS_FALLBACK: AboutMetricsResponse = {
  searchableRegistrations: {
    value: 79975437,
    display: '79M+',
    label: 'Searchable Registrations',
    subtext: 'DVLA registrations searchable across current, prefix, suffix and dateless styles.',
    sourceStatus: 'fallback',
  },
  dealerPricesAnalysed: {
    value: 463048,
    display: '450k+',
    label: 'Dealer Prices Analysed',
    subtext: 'Dealer price observations analysed across the comparison network.',
    sourceStatus: 'fallback',
  },
  historicSalesRecords: {
    value: 377099,
    display: '375k+',
    label: 'Historic Sales Records',
    subtext: 'Auction and third-party sales records, excluding DVLA fixed-price stock movement logs.',
    sourceStatus: 'fallback',
  },
  yearsOfMarketData: {
    value: 1989,
    display: 'Since 1989',
    label: 'Years of Market Data',
    subtext: 'Historic pricing evidence from DVLA auction and market sales data.',
    sourceStatus: 'fallback',
  },
  dealerSources: {
    value: 41,
    display: '40+',
    label: 'Dealer Sources',
    subtext: 'Dealer pricing sources indexed for comparison.',
    sourceStatus: 'fallback',
  },
  plateInteractionsTracked: {
    value: 5137334,
    display: '5M+',
    label: 'Plate Interactions Tracked',
    subtext: 'Plate views, searches and interaction events tracked across PlateX.',
    sourceStatus: 'fallback',
  },
  updatedAt: '2026-06-16T00:00:00Z',
};
